import { useMemo } from "react"
import {
  CHAT_EXECUTION_STATUS,
  type ChatExecution,
} from "@stockline/shared/types/ai/chat-execution"
import type { ChatMedia } from "@stockline/shared/types/ai/chat-media"
import type { ChatMessage } from "@stockline/shared/types/ai/chat-message"
import { CHAT_EXECUTION_TABLE } from "../../../lib/offline/resources/ai/chat-execution/db"
import { CHAT_MEDIA_TABLE } from "../../../lib/offline/resources/ai/chat-media/db"
import { CHAT_MESSAGE_TABLE } from "../../../lib/offline/resources/ai/chat-message/db"
import { useAISync } from "../providers/AISyncProvider"

/** Coarse media category for the attachment-only list preview. */
export type ChatMediaKind = "image" | "audio" | "video" | "document"

export interface ChatSessionSummary {
  /** Latest execution is PENDING or RUNNING. */
  isRunning: boolean
  /** Latest execution is paused awaiting the user's answer. */
  isWaitingInput: boolean
  /** Latest message content, collapsed to a single line (for the list preview). */
  lastMessage?: string
  /**
   * When the session was BORN from a `notify_user` notification, this is that
   * notification's body collapsed to one line. A notify session is the only
   * path that seeds an ASSISTANT message at position 0 (every ordinary
   * conversation's position-0 message is the user's own first message), so
   * this doubles as an exact, schema-free marker of a notification session.
   * The list preview shows THIS instead of `lastMessage` so a notification
   * always reads as its original message — even after the user has chatted in
   * it and newer replies exist. Undefined for ordinary conversations.
   */
  notifyMessage?: string
  /**
   * Timestamp (ISO) of the latest message — drives the list's "x ago" time and
   * recency sort, so they reflect the last MESSAGE, not session writes like
   * marking a notification read (which would otherwise bump it to "now"/top).
   */
  lastMessageAt?: string
  /**
   * Set when the latest message is attachment-only (empty content): the kind
   * and name of its first attachment, so the list row can render a
   * "Photo" / "Message vocal" / filename subtitle instead of nothing.
   */
  lastMessageMediaKind?: ChatMediaKind
  lastMessageMediaName?: string
  /**
   * Lowercased concatenation of every message's content in the session — lets
   * the chat-list search match on conversation contents, not just the title.
   * Attachment names are included so media-only conversations stay findable.
   */
  searchText: string
}

/** Coarse category from a MIME type (parameters tolerated). */
export function chatMediaKind(mimeType: string): ChatMediaKind {
  const base = (mimeType.split(";")[0] ?? "").trim().toLowerCase()
  if (base.startsWith("image/")) return "image"
  if (base.startsWith("audio/")) return "audio"
  if (base.startsWith("video/")) return "video"
  return "document"
}

/**
 * Lowercased concatenation of every message's content, grouped by session.
 * Pulled out as a pure function so the chat-list content-search behaviour is
 * unit-testable without mocking the reactive Dexie layer.
 */
export function buildSessionSearchText(
  messages: Pick<ChatMessage, "sessionId" | "content">[]
): Map<string, string> {
  const parts = new Map<string, string[]>()
  for (const m of messages) {
    if (!m.content) continue
    const list = parts.get(m.sessionId) ?? []
    list.push(m.content.toLowerCase())
    parts.set(m.sessionId, list)
  }
  const out = new Map<string, string>()
  for (const [sessionId, list] of parts) out.set(sessionId, list.join(" "))
  return out
}

/**
 * The notification body per notify-born session: the content of its
 * position-0 message when that message is an ASSISTANT turn. Only the
 * `notify_user` path seeds an assistant message at position 0 (every ordinary
 * conversation's position-0 message is the user's own first message), so this
 * is an exact, schema-free marker of a notification session. Collapsed to a
 * single line for the list preview. Pulled out as a pure function so the
 * "preview shows the notification, not the latest reply" rule is unit-testable
 * without mocking the reactive Dexie layer.
 */
export function buildNotificationPreviews(
  messages: Pick<ChatMessage, "sessionId" | "position" | "role" | "content">[]
): Map<string, string> {
  const zeroBySession = new Map<string, Pick<ChatMessage, "role" | "content">>()
  for (const m of messages) {
    if (m.position === 0) zeroBySession.set(m.sessionId, m)
  }
  const out = new Map<string, string>()
  for (const [sessionId, m] of zeroBySession) {
    if (m.role !== "assistant") continue
    const preview = m.content?.replace(/\s+/g, " ").trim()
    if (preview) out.set(sessionId, preview)
  }
  return out
}

/**
 * Per-session run state + last-message preview for the chat list, keyed by
 * sessionId. Reads the executions and messages tables once and reduces to the
 * latest per session, avoiding an N+1 of useLatestChatExecution() /
 * useChatMessages() across list rows.
 */
export function useChatSessionSummaries(): Map<string, ChatSessionSummary> {
  const { reactive } = useAISync()
  const executions = reactive.useQuery<ChatExecution>(CHAT_EXECUTION_TABLE)
  const messages = reactive.useQuery<ChatMessage>(CHAT_MESSAGE_TABLE)
  const media = reactive.useQuery<ChatMedia>(CHAT_MEDIA_TABLE)

  return useMemo(() => {
    const latestExec = new Map<string, ChatExecution>()
    for (const e of executions) {
      const prev = latestExec.get(e.sessionId)
      if (!prev || e.createdAt > prev.createdAt) latestExec.set(e.sessionId, e)
    }

    // Latest message per session — `position` is the in-session order.
    const latestMsg = new Map<string, ChatMessage>()
    for (const m of messages) {
      const prev = latestMsg.get(m.sessionId)
      if (!prev || m.position > prev.position) latestMsg.set(m.sessionId, m)
    }
    const searchTextBySession = buildSessionSearchText(messages)
    const notifyPreviewBySession = buildNotificationPreviews(messages)

    // Attachment lookups: first media per message (attachment-only preview)
    // and per-session media names (so search matches media-only threads).
    const firstMediaByMessage = new Map<string, ChatMedia>()
    const mediaNamesBySession = new Map<string, string[]>()
    for (const item of media) {
      if (item.deletedAt) continue
      if (item.messageId && !firstMediaByMessage.has(item.messageId)) {
        firstMediaByMessage.set(item.messageId, item)
      }
      if (item.name) {
        const names = mediaNamesBySession.get(item.sessionId) ?? []
        names.push(item.name.toLowerCase())
        mediaNamesBySession.set(item.sessionId, names)
      }
    }

    const out = new Map<string, ChatSessionSummary>()
    const sessionIds = new Set<string>([
      ...latestExec.keys(),
      ...latestMsg.keys(),
    ])
    for (const sessionId of sessionIds) {
      const e = latestExec.get(sessionId)
      const last = latestMsg.get(sessionId)
      const content = last?.content
      const lastMessage = content?.replace(/\s+/g, " ").trim() || undefined
      // Attachment-only message → surface its first attachment for the preview.
      const lastMedia =
        !lastMessage && last ? firstMediaByMessage.get(last.id) : undefined
      const mediaNames = mediaNamesBySession.get(sessionId)
      const searchText = [
        searchTextBySession.get(sessionId) ?? "",
        ...(mediaNames ?? []),
      ]
        .filter(Boolean)
        .join(" ")
      out.set(sessionId, {
        isRunning:
          e?.status === CHAT_EXECUTION_STATUS.PENDING ||
          e?.status === CHAT_EXECUTION_STATUS.RUNNING,
        isWaitingInput: e?.status === CHAT_EXECUTION_STATUS.WAITING_INPUT,
        lastMessage,
        notifyMessage: notifyPreviewBySession.get(sessionId),
        lastMessageAt: last?.createdAt,
        lastMessageMediaKind: lastMedia
          ? chatMediaKind(lastMedia.mimeType)
          : undefined,
        lastMessageMediaName: lastMedia?.name || undefined,
        searchText,
      })
    }
    return out
  }, [executions, messages, media])
}
